// src/client/game/audio/Sfx.ts

import { SfxSynth } from "./SfxSynth";
import { FoodKind } from "../core/model";

// Global kill-switch for ALL SFX in the app.
let globalSfxEnabled = true;

export function setGlobalSfxEnabled(enabled: boolean): void {
  globalSfxEnabled = enabled;
}

export function getGlobalSfxEnabled(): boolean {
  return globalSfxEnabled;
}

export class Sfx {
  private synth = new SfxSynth();
  private isEnabledLocal: () => boolean;

  private readonly foodHandlers: Record<FoodKind, () => void>;

  /**
   * isEnabledLocal should represent per-user/scene setting (ConfigScene).
   * Global mute (setGlobalSfxEnabled(false)) overrides this.
   */
  constructor(isEnabled: () => boolean = () => true) {
    this.isEnabledLocal = isEnabled;

    // Build once (O(1) lookup), no per-call allocations.
    this.foodHandlers = {
      [FoodKind.Normal]: () => this.eatRed(),
      [FoodKind.Gold]: () => this.eatGold(),
      [FoodKind.Blue]: () => this.eatBlueShrink(),
      [FoodKind.Laser]: () => this.eatPurplePowerUp(),
    };
  }

  /** Combined check: global + local. */
  private isOn(): boolean {
    return globalSfxEnabled && this.isEnabledLocal();
  }

  ensure(): void {
    if (!this.isOn()) return;
    this.synth.ensure();
  }

  /** Louder movement tick. */
  crawl(seed: number): void {
    if (!this.isOn()) return;
    this.ensure();

    const notes = [220, 247, 262, 294]; // A3 B3 C4 D4-ish
    const freq = notes[Math.abs(seed) % notes.length];
    this.synth.beep({
      freq,
      durMs: 24,
      gain: 0.5,
      type: "square",
      attackMs: 2,
      decayMs: 20,
    });
  }

  /** Pew-style laser. */
  laserPew(): void {
    if (!this.isOn()) return;
    this.ensure();

    // Crisp "pew" blip
    this.synth.beep({
      freq: 1200,
      durMs: 35,
      type: "square",
      gain: 0.7,
      attackMs: 1,
      decayMs: 30,
    });

    // Small tail
    setTimeout(() => {
      if (!this.isOn()) return;
      this.ensure();
      this.synth.beep({
        freq: 800,
        durMs: 45,
        type: "triangle",
        gain: 0.35,
        attackMs: 1,
        decayMs: 40,
      });
    }, 18);
  }

  /** Back-compat if any old code calls sfx.laser(). */
  laser(): void {
    this.laserPew();
  }

  /** Food-specific sound routing. */
  eatFood(kind: FoodKind): void {
    if (!this.isOn()) return;
    const fn = this.foodHandlers[kind] ?? this.foodHandlers[FoodKind.Normal];
    fn();
  }

  private eatRed(): void {
    this.ensure();
    this.synth.arpeggio([523, 659, 784], 45, "square"); // "cha-ching"
  }

  private eatGold(): void {
    this.ensure();
    this.synth.arpeggio([659, 784, 988], 40, "square"); // bigger ching
  }

  private eatBlueShrink(): void {
    this.ensure();
    this.synth.arpeggio([392, 330, 262], 55, "triangle"); // shrinking descent
  }

  private eatPurplePowerUp(): void {
    this.ensure();
    this.synth.arpeggio([440, 554, 659, 880], 35, "square"); // power-up rise
  }

  gameOver(): void {
    if (!this.isOn()) return;
    this.ensure();
    this.synth.arpeggio([392, 330, 262], 90, "triangle");
  }
}
